import React, { useEffect, useRef } from 'react';

import type { PayPalButtonsComponent } from '@paypal/paypal-js';
import type { PayPalButtonsComponentProps } from '@paypal/react-paypal-js';

import { cn } from '@change/ds-utils';

import { getWindow } from '@change-corgi/core/window';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { PaypalButtonLoading } from '../PaypalButtonLoading';

type Props = PayPalButtonsComponentProps & {
	loading?: boolean;
};

export const PaypalButtonSDK: React.FC<Props> = ({ loading, ...props }) => {
	const window = getWindow();
	const utilityContext = useUtilityContext();
	const containerRef = useRef<HTMLDivElement>(null);
	const buttonsRef = useRef<PayPalButtonsComponent | null>(null);

	useEffect(() => {
		// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
		if (!window.paypal || !containerRef.current || buttonsRef.current) return undefined;

		const buttons = window.paypal.Buttons(props);
		buttonsRef.current = buttons;
		void buttons.render(containerRef.current);

		return () => {
			// oxlint-disable-next-line promise/prefer-await-to-then -- effect cleanup must be synchronous, so `await` isn't available here
			void buttons.close().catch((error) => {
				void utilityContext.errorReporter.report(
					{ error: new Error('PayPal Buttons Close Error', { cause: error }) },
					{ severity: 'warning' },
				);
			});
			buttonsRef.current = null;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [window.paypal]);

	useEffect(() => {
		if (!buttonsRef.current) return;
		void buttonsRef.current.updateProps(props);
	}, [props]);

	// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
	if (!window.paypal) return null;

	// Box with negative margin here to remove extra padding added by paypal sdk
	return (
		<div className="mb-[-5px]" data-qa="paypal-button-sdk-native">
			{loading && <PaypalButtonLoading style={props.style} />}
			<div
				className={cn({ hidden: loading })}
				data-testid="paypal-driver-button-wrapper"
				data-qa="paypal-submit-button"
				style={{ colorScheme: 'none' }}
			>
				<div ref={containerRef} />
			</div>
		</div>
	);
};
